import type { UvTransform, Vec2 } from '../../lib/uv-doc.js';
import type { UvView } from '../../state/uv.js';
import { gridStep, uvToScreen } from './uv-draw.js';

/**
 * Blender-style modal transforms of selected UVs (G / R / S): the mouse sets
 * the amount relative to where the transform started, X / Y constrain to U / V,
 * typing a number sets it exactly, Ctrl snaps (grid steps, 5°, 0.1).
 */

export type ModalKind = 'grab' | 'rotate' | 'scale';

export interface ModalState {
  kind: ModalKind;
  axis: 'u' | 'v' | null;
  /** Canvas position (CSS px) where the transform started. */
  start: Vec2;
  pivot: Vec2;
  /** Typed value ("" = follow the mouse). */
  typed: string;
  /** Started by dragging a selected element: releasing the button confirms. */
  tweak: boolean;
  indices: number[];
  base: Vec2[];
}

const fmt = (n: number) => {
  const r = Math.round(n * 10000) / 10000;
  return (Object.is(r, -0) ? 0 : r).toString();
};

/** Typed value, or null while it is empty / just a sign. */
export function typedValue(typed: string): number | null {
  if (!typed || typed === '-' || typed === '.' || typed === '-.') return null;
  const n = Number(typed);
  return Number.isFinite(n) ? n : null;
}

/** Edit the typed value with one key; null when the key is not part of a number. */
export function typeKey(typed: string, key: string): string | null {
  if (/^[0-9]$/.test(key)) return typed + key;
  if (key === '.' || key === ',') return typed.includes('.') ? typed : `${typed}.`;
  if (key === '-') return typed.startsWith('-') ? typed.slice(1) : `-${typed}`;
  if (key === 'Backspace') return typed.slice(0, -1);
  return null;
}

const KIND_LABEL: Record<ModalKind, string> = { grab: 'Move', rotate: 'Rotate', scale: 'Scale' };

/** Transform for the current mouse position, with the status text. */
export function modalTransform(m: ModalState, mouse: Vec2, view: UvView, size: Vec2, snap: boolean): { transform: UvTransform; text: string } {
  const typed = typedValue(m.typed);
  const axisText = m.axis ? ` along ${m.axis.toUpperCase()}` : '';
  const typedText = m.typed ? ` [${m.typed}]` : '';
  if (m.kind === 'grab') {
    let d: Vec2 = [(mouse[0] - m.start[0]) / view.zoom, -(mouse[1] - m.start[1]) / view.zoom];
    if (snap) {
      const step = gridStep(view.zoom);
      d = [Math.round(d[0] / step) * step, Math.round(d[1] / step) * step];
    }
    if (typed !== null) d = m.axis === 'v' ? [0, typed] : [typed, 0];
    if (m.axis === 'u') d = [d[0], 0];
    if (m.axis === 'v') d = [0, d[1]];
    return { transform: { kind: 'translate', delta: d }, text: `${KIND_LABEL.grab}${axisText}${typedText}  U ${fmt(d[0])}  V ${fmt(d[1])}` };
  }
  const p = uvToScreen(view, size, m.pivot);
  if (m.kind === 'rotate') {
    const a0 = Math.atan2(m.start[1] - p[1], m.start[0] - p[0]);
    const a1 = Math.atan2(mouse[1] - p[1], mouse[0] - p[0]);
    // Canvas y points down: a clockwise screen turn is a negative UV angle.
    let angle = -(a1 - a0);
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle <= -Math.PI) angle += 2 * Math.PI;
    let deg = (angle * 180) / Math.PI;
    if (snap) deg = Math.round(deg / 5) * 5;
    if (typed !== null) deg = typed;
    return { transform: { kind: 'rotate', angle: (deg * Math.PI) / 180, pivot: m.pivot }, text: `${KIND_LABEL.rotate}${typedText}  ${fmt(deg)}°` };
  }
  const d0 = Math.hypot(m.start[0] - p[0], m.start[1] - p[1]);
  const d1 = Math.hypot(mouse[0] - p[0], mouse[1] - p[1]);
  let f = d0 > 1e-6 ? d1 / d0 : 1;
  if (snap) f = Math.round(f * 10) / 10;
  if (typed !== null) f = typed;
  const scale: Vec2 = m.axis === 'u' ? [f, 1] : m.axis === 'v' ? [1, f] : [f, f];
  return { transform: { kind: 'scale', scale, pivot: m.pivot }, text: `${KIND_LABEL.scale}${axisText}${typedText}  ${fmt(scale[0])} × ${fmt(scale[1])}` };
}
